import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Send, Inbox as InboxIcon, User, Clock, Check, CheckCheck, X,
  AlertCircle, Loader2, Plus, FileText, ChevronDown, RefreshCw,
  Image as ImageIcon, Mic, Video as VideoIcon, MapPin, Phone as PhoneIcon,
  Download, Sticker, Search
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_INTERVAL = 5000;
const SEARCH_DEBOUNCE_MS = 400;

// ─── Media Message Renderer ──────────────────────────────────────────────────
// Fetches media via authenticated backend proxy and renders the appropriate element.
const MediaMessage = ({ message, isOutgoing }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const type = message.content_type;
  const caption = message.caption || message.content || null;

  useEffect(() => {
    if (!message.media_url) { setLoading(false); setError(true); return; }
    let cancelled = false;
    setLoading(true); setError(false);
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${API_URL}/media/${message.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [message.id, message.media_url]); // eslint-disable-line

  const textClass = isOutgoing ? 'text-white/70' : 'text-[#8A8A8B]';
  const captionClass = isOutgoing ? 'text-white/90' : 'text-[#131517]';

  if (loading) return (
    <div className="flex items-center gap-2 py-1">
      <Loader2 className={`w-4 h-4 animate-spin ${isOutgoing ? 'text-white/60' : 'text-[#8A8A8B]'}`} />
      <span className={`text-[12px] ${textClass}`}>Loading {type}…</span>
    </div>
  );

  if (error || !blobUrl) {
    const Icon = type === 'audio' ? Mic : type === 'video' ? VideoIcon : type === 'image' ? ImageIcon : FileText;
    return (
      <div className="flex items-center gap-2 py-1">
        <Icon className={`w-4 h-4 ${isOutgoing ? 'text-white/60' : 'text-[#8A8A8B]'}`} />
        <span className={`text-[12px] ${textClass}`}>[{type} — unable to load]</span>
      </div>
    );
  }

  if (type === 'image' || type === 'sticker') return (
    <div className="space-y-1">
      <img
        src={blobUrl}
        alt={caption || type}
        onClick={() => setExpanded(true)}
        className="max-w-[240px] max-h-[300px] rounded-lg object-cover cursor-zoom-in block"
      />
      {caption && <p className={`text-[13px] mt-1 leading-relaxed ${captionClass}`}>{caption}</p>}
      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setExpanded(false)}>
          <img src={blobUrl} alt={caption || 'image'} className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
          <button className="absolute top-4 right-4 text-white" onClick={() => setExpanded(false)}><X className="w-6 h-6" /></button>
        </div>
      )}
    </div>
  );

  if (type === 'audio') return (
    <div className="py-0.5">
      <audio controls src={blobUrl} className="max-w-[260px] h-9" style={{ filter: isOutgoing ? 'invert(1) brightness(0.85)' : 'none' }} />
    </div>
  );

  if (type === 'video') return (
    <div className="space-y-1">
      <video controls src={blobUrl} className="max-w-[240px] max-h-[200px] rounded-lg block" />
      {caption && <p className={`text-[13px] leading-relaxed ${captionClass}`}>{caption}</p>}
    </div>
  );

  if (type === 'document') return (
    <a href={blobUrl} download={message.content || 'document'} className="flex items-center gap-2 py-1 group">
      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${isOutgoing ? 'bg-white/20' : 'bg-[#EB493D]/10'}`}>
        <FileText className={`w-4 h-4 ${isOutgoing ? 'text-white' : 'text-[#EB493D]'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[13px] font-medium truncate ${captionClass}`}>{message.content || 'Document'}</p>
        {caption && caption !== message.content && <p className={`text-[11px] ${textClass}`}>{caption}</p>}
        <p className={`text-[11px] ${textClass} group-hover:underline`}>Tap to download</p>
      </div>
      <Download className={`w-4 h-4 flex-shrink-0 ${isOutgoing ? 'text-white/70' : 'text-[#595C5C]'}`} />
    </a>
  );

  return <span className={`text-[13px] ${textClass}`}>[{type}]</span>;
};

// ─── Location Message ────────────────────────────────────────────────────────
const LocationMessage = ({ message, isOutgoing }) => {
  const [lat, lng] = (message.media_url || ',').split(',');
  const mapsUrl = lat && lng ? `https://maps.google.com/?q=${lat},${lng}` : null;
  const textClass = isOutgoing ? 'text-white/90' : 'text-[#131517]';
  const subClass = isOutgoing ? 'text-white/60' : 'text-[#8A8A8B]';
  return (
    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 py-0.5 group">
      <MapPin className={`w-5 h-5 flex-shrink-0 ${isOutgoing ? 'text-white/70' : 'text-[#EB493D]'}`} />
      <div>
        <p className={`text-[13px] font-medium ${textClass}`}>{message.content || 'Location'}</p>
        {mapsUrl && <p className={`text-[11px] ${subClass} group-hover:underline`}>Open in Maps</p>}
      </div>
    </a>
  );
};

// ─── Contact Message ─────────────────────────────────────────────────────────
const ContactMessage = ({ message, isOutgoing }) => (
  <div className="flex items-center gap-2 py-0.5">
    <PhoneIcon className={`w-4 h-4 flex-shrink-0 ${isOutgoing ? 'text-white/70' : 'text-[#595C5C]'}`} />
    <p className={`text-[13px] ${isOutgoing ? 'text-white/90' : 'text-[#131517]'}`}>{message.content}</p>
  </div>
);

// ─── Template Header Viewer Modal ─────────────────────────────────────────────
const TemplateHeaderViewer = ({ media_url, header_type, onClose }) => {
  const isPdf = media_url && media_url.toLowerCase().includes('.pdf');

  // Prevent click-inside from closing
  const stopProp = e => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        className="absolute top-4 right-4 z-10 text-white bg-black/40 hover:bg-black/60 rounded-full p-1.5 transition-colors"
        onClick={onClose}
      >
        <X className="w-5 h-5" />
      </button>

      {header_type === 'image' && (
        <img
          src={media_url}
          alt="Template header"
          className="max-w-[92vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
          onClick={stopProp}
        />
      )}

      {header_type === 'video' && (
        <video
          src={media_url}
          controls
          autoPlay
          className="max-w-[92vw] max-h-[90vh] rounded-lg shadow-2xl"
          onClick={stopProp}
        />
      )}

      {header_type === 'document' && (
        <div
          className="bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
          style={{ width: '90vw', height: '90vh' }}
          onClick={stopProp}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
            <span className="text-[13px] font-medium text-[#131517] truncate mr-4">Document Preview</span>
            <a
              href={media_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[12px] text-[#595C5C] hover:text-[#131517] flex-shrink-0"
            >
              <Download className="w-3.5 h-3.5" />
              Open / Download
            </a>
          </div>
          {isPdf ? (
            <iframe
              src={media_url}
              title="Document"
              className="flex-1 w-full border-0"
            />
          ) : (
            // Non-PDF: show a friendly fallback with an open link
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
              <FileText className="w-16 h-16 text-[#D9E2E5]" />
              <p className="text-[14px] text-[#595C5C] text-center">
                This file type cannot be previewed in-app.
              </p>
              <a
                href={media_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#131517] text-white text-[13px] rounded-lg hover:bg-[#595C5C] transition-colors"
              >
                <Download className="w-4 h-4" />
                Open in new tab
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Template Message Renderer ───────────────────────────────────────────────
// Renders a WhatsApp template message exactly like the end-user sees it:
//   header (image / video / document / bold text)  ← if present, clickable to open viewer
//   body text (rendered with variables)
//   footer (light text, smaller)                   ← if present
//
// Direct-URL rendering (isPublicUrl) is ONLY used for mirror-payload templates where
// media_url is a public CDN https:// link. All other media (WhatsApp media IDs) are
// routed through the backend proxy via MediaMessage — unchanged behaviour.
const TemplateMessage = ({ message, isOutgoing }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const {
    content,
    media_url,
    template_header_type,
    template_header_text,
    template_footer_text,
  } = message;

  const bodyClass    = isOutgoing ? 'text-white'     : 'text-[#131517]';
  const footerClass  = isOutgoing ? 'text-white/50'  : 'text-[#8A8A8B]';
  const dividerClass = isOutgoing ? 'border-white/20' : 'border-[#D9E2E5]';

  // Mirror-payload templates provide public CDN URLs; WhatsApp media IDs are proxied.
  const isPublicUrl = media_url && (media_url.startsWith('http://') || media_url.startsWith('https://'));
  const isViewable  = isPublicUrl && (template_header_type === 'image' || template_header_type === 'video' || template_header_type === 'document');

  const openViewer  = e => { e.stopPropagation(); setViewerOpen(true); };

  const renderHeader = () => {
    if (!template_header_type) return null;

    // ── Image ────────────────────────────────────────────────────────────────
    if (template_header_type === 'image' && media_url) {
      if (isPublicUrl) {
        return (
          <div className="-mx-3 -mt-2 mb-0 relative group cursor-zoom-in" onClick={openViewer}>
            <img
              src={media_url}
              alt="Template header"
              className="w-full max-h-[220px] object-cover rounded-t-lg block"
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-t-lg flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 text-white text-[11px] bg-black/50 px-2 py-1 rounded transition-opacity">
                Click to expand
              </span>
            </div>
          </div>
        );
      }
      return <MediaMessage message={{ ...message, content_type: 'image' }} isOutgoing={isOutgoing} />;
    }

    // ── Video ────────────────────────────────────────────────────────────────
    if (template_header_type === 'video' && media_url) {
      if (isPublicUrl) {
        return (
          <div className="-mx-3 -mt-2 mb-0 relative group cursor-pointer" onClick={openViewer}>
            <video
              src={media_url}
              className="w-full max-h-[220px] rounded-t-lg block pointer-events-none"
            />
            {/* Play overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors rounded-t-lg">
              <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current ml-1">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
        );
      }
      return <MediaMessage message={{ ...message, content_type: 'video' }} isOutgoing={isOutgoing} />;
    }

    // ── Document / PDF ────────────────────────────────────────────────────────
    if (template_header_type === 'document' && media_url) {
      if (isPublicUrl) {
        return (
          <button
            onClick={openViewer}
            className={`w-full flex items-center gap-2 py-2 px-1 mb-1 group text-left hover:opacity-80 transition-opacity`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isOutgoing ? 'bg-white/20' : 'bg-[#EB493D]/10'}`}>
              <FileText className={`w-5 h-5 ${isOutgoing ? 'text-white' : 'text-[#EB493D]'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[13px] font-medium truncate ${bodyClass}`}>Document</p>
              <p className={`text-[11px] ${footerClass}`}>Tap to view</p>
            </div>
            <Download className={`w-4 h-4 flex-shrink-0 opacity-60 ${isOutgoing ? 'text-white' : 'text-[#595C5C]'}`} />
          </button>
        );
      }
      return <MediaMessage message={{ ...message, content_type: 'document' }} isOutgoing={isOutgoing} />;
    }

    // ── Text header ───────────────────────────────────────────────────────────
    if (template_header_type === 'text' && template_header_text) {
      return (
        <p className={`text-[14px] font-bold leading-snug mb-2 ${bodyClass}`}>
          {template_header_text}
        </p>
      );
    }
    return null;
  };

  const headerEl = renderHeader();
  const hasMediaHeader = headerEl !== null && template_header_type !== 'text';

  return (
    <>
      <div className="space-y-0 min-w-[180px] max-w-[260px]">
        {headerEl}
        {content && (
          <p className={`text-[13px] whitespace-pre-wrap leading-relaxed ${bodyClass} ${hasMediaHeader ? 'pt-2' : ''}`}>
            {content}
          </p>
        )}
        {/* Hint when body is just a fallback template identifier (template not in local DB) */}
        {content && content.startsWith('Template: ') && (
          <p className={`text-[11px] mt-1 italic ${isOutgoing ? 'text-white/50' : 'text-[#8A8A8B]'}`}>
            Full content unavailable — sync templates to view
          </p>
        )}
        {template_footer_text && (
          <>
            <div className={`border-t ${dividerClass} mt-1.5 mb-1`} />
            <p className={`text-[11px] ${footerClass}`}>{template_footer_text}</p>
          </>
        )}
      </div>

      {/* Full-screen viewer — rendered when header is clicked */}
      {viewerOpen && isViewable && (
        <TemplateHeaderViewer
          media_url={media_url}
          header_type={template_header_type}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
};

// ─── Message Content Renderer ────────────────────────────────────────────────
const MessageContent = ({ message }) => {
  const isOutgoing = message.type === 'OUTGOING';
  const { content_type, content } = message;

  if (content_type === 'template') {
    return <TemplateMessage message={message} isOutgoing={isOutgoing} />;
  }
  if (['image', 'audio', 'video', 'document', 'sticker'].includes(content_type)) {
    return <MediaMessage message={message} isOutgoing={isOutgoing} />;
  }
  if (content_type === 'location') {
    return <LocationMessage message={message} isOutgoing={isOutgoing} />;
  }
  if (content_type === 'contacts') {
    return <ContactMessage message={message} isOutgoing={isOutgoing} />;
  }
  // Default: plain text
  return <p className="text-[14px] whitespace-pre-wrap leading-relaxed">{content}</p>;
};

// ─── Helper: extract variable count from template body ──────────────────────
const extractVariables = (text = '') => {
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  const nums = [...new Set(matches.map(m => parseInt(m.replace(/\D/g, ''))))].sort((a,b)=>a-b);
  return nums;
};

// ─── Template Send Modal ─────────────────────────────────────────────────────
const TemplateSendModal = ({ open, onClose, onSend, templates, sending }) => {
  const [selected, setSelected] = useState(null);
  const [bodyVars, setBodyVars] = useState({});

  useEffect(() => {
    if (!open) { setSelected(null); setBodyVars({}); }
  }, [open]);

  useEffect(() => {
    if (selected) {
      const vars = extractVariables(selected.body_text);
      const initial = {};
      vars.forEach(n => { initial[n] = ''; });
      setBodyVars(initial);
    }
  }, [selected]);

  const buildComponents = () => {
    const components = [];
    const bodyVarNums = extractVariables(selected.body_text);
    if (bodyVarNums.length > 0) {
      components.push({
        type: 'body',
        parameters: bodyVarNums.map(n => ({ type: 'text', text: bodyVars[n] || '' }))
      });
    }
    return components;
  };

  const approvedTemplates = templates.filter(t => t.status === 'APPROVED');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#131517]">Send Template Message</DialogTitle>
          <DialogDescription className="text-[13px] text-[#8A8A8B]">
            Choose an approved template and fill in any variables.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {approvedTemplates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 mx-auto text-[#D9E2E5] mb-2" />
              <p className="text-[14px] text-[#8A8A8B]">No approved templates</p>
              <p className="text-[12px] text-[#AFB0B0] mt-1">Templates must be approved by WhatsApp before use</p>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-[#131517] text-[13px] font-medium mb-2 block">Select Template</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {approvedTemplates.map(t => (
                    <div
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selected?.id === t.id
                          ? 'border-[#131517] bg-[#131517]/5'
                          : 'border-[#D9E2E5] hover:border-[#595C5C]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-[#131517]">{t.name}</span>
                        <span className="text-[10px] text-[#8A8A8B] bg-[#EFEFEF] px-2 py-0.5 rounded">{t.category}</span>
                      </div>
                      <p className="text-[12px] text-[#595C5C] mt-1 line-clamp-2">{t.body_text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {selected && extractVariables(selected.body_text).length > 0 && (
                <div>
                  <Label className="text-[#131517] text-[13px] font-medium mb-2 block">Fill Variables</Label>
                  <div className="space-y-2">
                    {extractVariables(selected.body_text).map(n => (
                      <div key={n} className="flex items-center gap-2">
                        <span className="text-[12px] text-[#8A8A8B] w-16 flex-shrink-0">{`{{${n}}}`}</span>
                        <Input
                          value={bodyVars[n] || ''}
                          onChange={e => setBodyVars(prev => ({ ...prev, [n]: e.target.value }))}
                          placeholder={`Variable ${n}`}
                          className="border-[#D9E2E5] h-8 text-[13px]"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected && (
                <div className="bg-[#F8F9FA] rounded-lg p-3 border border-[#D9E2E5]">
                  <p className="text-[10px] font-semibold text-[#8A8A8B] uppercase mb-1">Preview</p>
                  <p className="text-[13px] text-[#131517] leading-relaxed whitespace-pre-wrap">
                    {selected.body_text.replace(/\{\{(\d+)\}\}/g, (_, n) => bodyVars[parseInt(n)] || `{{${n}}}`)}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-[#D9E2E5]">
                <Button variant="outline" onClick={onClose} className="border-[#D9E2E5]">Cancel</Button>
                <Button
                  onClick={() => onSend(selected, buildComponents())}
                  disabled={!selected || sending}
                  className="bg-[#131517] hover:bg-[#333537]"
                  data-testid="send-template-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Template
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── New Conversation Modal ───────────────────────────────────────────────────
const NewConversationModal = ({ open, onClose, onStart, templates, starting }) => {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(null);
  const [bodyVars, setBodyVars] = useState({});

  useEffect(() => {
    if (!open) { setPhone(''); setName(''); setSelected(null); setBodyVars({}); }
  }, [open]);

  useEffect(() => {
    if (selected) {
      const vars = extractVariables(selected.body_text);
      const initial = {};
      vars.forEach(n => { initial[n] = ''; });
      setBodyVars(initial);
    }
  }, [selected]);

  const buildComponents = () => {
    const components = [];
    const bodyVarNums = extractVariables(selected?.body_text || '');
    if (bodyVarNums.length > 0) {
      components.push({
        type: 'body',
        parameters: bodyVarNums.map(n => ({ type: 'text', text: bodyVars[n] || '' }))
      });
    }
    return components;
  };

  const approvedTemplates = templates.filter(t => t.status === 'APPROVED');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#131517]">New Conversation</DialogTitle>
          <DialogDescription className="text-[13px] text-[#8A8A8B]">
            Enter the contact's phone number and select a template to start a new conversation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#131517] text-[13px]">Phone Number *</Label>
              <Input
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+1234567890"
                className="border-[#D9E2E5]"
                data-testid="new-conv-phone"
              />
              <p className="text-[10px] text-[#8A8A8B]">Include country code</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#131517] text-[13px]">Contact Name (optional)</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="John Doe"
                className="border-[#D9E2E5]"
              />
            </div>
          </div>

          <div>
            <Label className="text-[#131517] text-[13px] font-medium mb-2 block">Select Template *</Label>
            {approvedTemplates.length === 0 ? (
              <p className="text-[13px] text-[#8A8A8B] p-3 bg-[#F8F9FA] rounded-lg border border-[#D9E2E5]">
                No approved templates available. Templates must be approved by WhatsApp.
              </p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                {approvedTemplates.map(t => (
                  <div
                    key={t.id}
                    onClick={() => setSelected(t)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selected?.id === t.id
                        ? 'border-[#131517] bg-[#131517]/5'
                        : 'border-[#D9E2E5] hover:border-[#595C5C]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-medium text-[#131517]">{t.name}</span>
                      <span className="text-[10px] text-[#8A8A8B] bg-[#EFEFEF] px-2 py-0.5 rounded">{t.category}</span>
                    </div>
                    <p className="text-[12px] text-[#595C5C] mt-1 line-clamp-1">{t.body_text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selected && extractVariables(selected.body_text).length > 0 && (
            <div>
              <Label className="text-[#131517] text-[13px] font-medium mb-2 block">Fill Variables</Label>
              <div className="space-y-2">
                {extractVariables(selected.body_text).map(n => (
                  <div key={n} className="flex items-center gap-2">
                    <span className="text-[12px] text-[#8A8A8B] w-16 flex-shrink-0">{`{{${n}}}`}</span>
                    <Input
                      value={bodyVars[n] || ''}
                      onChange={e => setBodyVars(prev => ({ ...prev, [n]: e.target.value }))}
                      placeholder={`Variable ${n}`}
                      className="border-[#D9E2E5] h-8 text-[13px]"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#D9E2E5]">
            <Button variant="outline" onClick={onClose} className="border-[#D9E2E5]">Cancel</Button>
            <Button
              onClick={() => onStart(phone, name, selected, buildComponents())}
              disabled={!phone.trim() || !selected || starting}
              className="bg-[#131517] hover:bg-[#333537]"
              data-testid="start-conversation-btn"
            >
              {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Start Conversation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main InboxPage ───────────────────────────────────────────────────────────
const InboxPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [windowStatus, setWindowStatus] = useState('unknown'); // 'open' | 'unknown' | 'expired'
  const [templates, setTemplates] = useState([]);
  const [showTemplateSend, setShowTemplateSend] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const messagesContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const messagesRef = useRef([]);
  const isPrependingRef = useRef(false);
  const justSentRef = useRef(false);
  const hasMoreMessagesRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const selectedConvRef = useRef(null);
  const pollingRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const convListRef = useRef(null);
  const convCountRef = useRef(PAGE_SIZE); // track how many convs are loaded for polling
  const activeSearchRef = useRef(''); // keep search in sync for polling

  // Keep refs in sync
  useEffect(() => { selectedConvRef.current = selectedConv; }, [selectedConv]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { hasMoreMessagesRef.current = hasMoreMessages; }, [hasMoreMessages]);
  useEffect(() => { loadingMoreRef.current = loadingMoreMessages; }, [loadingMoreMessages]);

  // Scroll to bottom of messages container
  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Smart auto-scroll: only scroll when user is already near bottom or just sent a message
  useEffect(() => {
    if (isPrependingRef.current) {
      isPrependingRef.current = false;
      return;
    }
    if (justSentRef.current || isAtBottomRef.current) {
      justSentRef.current = false;
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Initial load
  useEffect(() => {
    fetchConversations('', 0, false);
    fetchTemplates();
    pollingRef.current = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(pollingRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    // Only query DB when query is empty (show all) or has at least 5 characters
    if (searchQuery.length > 0 && searchQuery.length < 5) return;
    searchDebounceRef.current = setTimeout(() => {
      activeSearchRef.current = searchQuery;
      fetchConversations(searchQuery, 0, false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchConversations = async (search = '', skip = 0, append = false, silent = false) => {
    if (!append) {
      if (!silent) setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (search && search.trim()) params.search = search.trim();
      const response = await axios.get(`${API_URL}/conversations`, { params });
      const { items, has_more } = response.data;
      if (append) {
        setConversations(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newItems = items.filter(c => !existingIds.has(c.id));
          const merged = [...prev, ...newItems];
          convCountRef.current = merged.length;
          return merged;
        });
      } else {
        setConversations(items);
        convCountRef.current = items.length;
      }
      setHasMore(has_more);
    } catch (error) {
      if (!silent && !append) toast.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (_) {}
  };

  const poll = useCallback(async () => {
    // Silently refresh conversations list — refresh all currently loaded
    try {
      const search = activeSearchRef.current;
      const currentCount = convCountRef.current;
      const params = { skip: 0, limit: Math.max(PAGE_SIZE, currentCount) };
      if (search && search.trim()) params.search = search.trim();
      const res = await axios.get(`${API_URL}/conversations`, { params });
      setConversations(res.data.items || []);
      convCountRef.current = (res.data.items || []).length;
    } catch (_) {}
    // Refresh active conversation: only update window_status and append new messages
    const conv = selectedConvRef.current;
    if (conv) {
      // 1. Refresh window_status
      try {
        const res = await axios.get(`${API_URL}/conversations/${conv.id}`);
        setWindowStatus(res.data.window_status || 'unknown');
      } catch (_) {}
      // 2. Fetch only messages newer than what we have (avoids resetting the message list)
      const currentMsgs = messagesRef.current;
      const newest = currentMsgs[currentMsgs.length - 1];
      if (newest?.created_at) {
        try {
          const res = await axios.get(`${API_URL}/conversations/${conv.id}/messages`, {
            params: { after: newest.created_at, limit: 50 }
          });
          const newMsgs = res.data.messages || [];
          if (newMsgs.length > 0) {
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const truly_new = newMsgs.filter(m => !existingIds.has(m.id));
              return truly_new.length > 0 ? [...prev, ...truly_new] : prev;
            });
          }
        } catch (_) {}
      }
    }
  }, []);

  // Infinite scroll handler
  const handleConvScroll = useCallback((e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (nearBottom && hasMore && !loadingMore) {
      const skip = conversations.length;
      fetchConversations(activeSearchRef.current, skip, true, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, conversations.length]);

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    isAtBottomRef.current = true;
    setHasMoreMessages(false);
    setLoadingMoreMessages(false);
    try {
      const response = await axios.get(`${API_URL}/conversations/${conv.id}`);
      setMessages(response.data.messages || []);
      setHasMoreMessages(response.data.has_more_messages || false);
      setWindowStatus(response.data.window_status || 'unknown');
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      requestAnimationFrame(() => scrollToBottom());
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  // Load older messages when user scrolls to top
  const loadOlderMessages = useCallback(async () => {
    const conv = selectedConvRef.current;
    if (!conv || !hasMoreMessagesRef.current || loadingMoreRef.current) return;
    const currentMsgs = messagesRef.current;
    const oldest = currentMsgs[0];
    if (!oldest?.created_at) return;
    setLoadingMoreMessages(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    const prevScrollTop = container?.scrollTop || 0;
    try {
      const res = await axios.get(`${API_URL}/conversations/${conv.id}/messages`, {
        params: { before: oldest.created_at, limit: 10 }
      });
      const { messages: olderMsgs, has_more } = res.data;
      if (olderMsgs?.length > 0) {
        isPrependingRef.current = true;
        setMessages(prev => [...olderMsgs, ...prev]);
        setHasMoreMessages(has_more || false);
        // Restore scroll position so content doesn't jump
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = prevScrollTop + (container.scrollHeight - prevScrollHeight);
          }
        });
      } else {
        setHasMoreMessages(false);
      }
    } catch (_) {}
    finally { setLoadingMoreMessages(false); }
  }, []);

  // Handle scroll events on the messages container
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distFromBottom < 80;
    // Trigger load of older messages when scrolled near the top
    if (el.scrollTop < 80 && hasMoreMessagesRef.current && !loadingMoreRef.current) {
      loadOlderMessages();
    }
  }, [loadOlderMessages]);

  const sendTextMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    setSending(true);
    justSentRef.current = true;
    try {
      const response = await axios.post(`${API_URL}/conversations/${selectedConv.id}/messages`, {
        content: newMessage,
        content_type: 'text'
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setConversations(prev => prev.map(c =>
        c.id === selectedConv.id
          ? { ...c, last_message_preview: newMessage.substring(0, 100), last_message_at: new Date().toISOString() }
          : c
      ));
    } catch (error) {
      justSentRef.current = false;
      if (error.response?.data?.detail?.includes('window')) {
        setWindowStatus('expired');
        toast.error('Conversation window closed. Please use a template message.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const sendTemplateMessage = async (template, components) => {
    if (!template || !selectedConv) return;
    setSending(true);
    justSentRef.current = true;
    try {
      const response = await axios.post(`${API_URL}/conversations/${selectedConv.id}/messages`, {
        content: template.name,
        content_type: 'template',
        template_name: template.name,
        template_components: components
      });
      setMessages(prev => [...prev, response.data]);
      setShowTemplateSend(false);
      toast.success('Template message sent');
      setConversations(prev => prev.map(c =>
        c.id === selectedConv.id
          ? { ...c, last_message_preview: `[Template: ${template.name}]`, last_message_at: new Date().toISOString() }
          : c
      ));
    } catch (error) {
      justSentRef.current = false;
      toast.error(error.response?.data?.detail || 'Failed to send template');
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async (phone, name, template, components) => {
    if (!phone || !template) return;
    setStarting(true);
    try {
      const response = await axios.post(`${API_URL}/conversations`, {
        contact_phone: phone,
        contact_name: name || null,
        template_name: template.name,
        template_components: components,
        template_language: template.language || 'en_US'
      });
      setShowNewConv(false);
      toast.success('Conversation started');
      await fetchConversations(activeSearchRef.current, 0, false, true);
      // Auto-select the new conversation
      setSelectedConv(response.data);
      isAtBottomRef.current = true;
      const full = await axios.get(`${API_URL}/conversations/${response.data.id}`);
      setMessages(full.data.messages || []);
      setHasMoreMessages(full.data.has_more_messages || false);
      setWindowStatus(full.data.window_status || 'unknown');
      requestAnimationFrame(() => scrollToBottom());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setStarting(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    return d.toLocaleString([], {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusIcon = (msg) => {
    const { status, delivered_at, read_at } = msg;

    const lines = [];
    if (status === 'DELIVERED' || status === 'READ') {
      if (delivered_at) lines.push(`Delivered ${formatDateTime(delivered_at)}`);
    }
    if (status === 'READ') {
      if (read_at) lines.push(`Read ${formatDateTime(read_at)}`);
    }

    let icon;
    switch (status) {
      case 'SENT':
        icon = <Check className="w-3 h-3" />;
        break;
      case 'DELIVERED':
        icon = <CheckCheck className="w-3 h-3" />;
        break;
      case 'READ':
        icon = <CheckCheck className="w-3 h-3 text-[#3BAB59]" />;
        break;
      case 'FAILED':
        return <X className="w-3 h-3 text-[#EB493D]" />;
      default:
        return <Clock className="w-3 h-3 opacity-40" />;
    }

    if (lines.length === 0) return icon;

    return (
      <div className="relative group/tick inline-flex items-center">
        {icon}
        <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover/tick:block z-50 pointer-events-none">
          <div className="bg-[#131517] text-white text-[11px] rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
            {lines.map((l, i) => <div key={i}>{l}</div>)}
            <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-[#131517]" />
          </div>
        </div>
      </div>
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="h-[calc(100vh-theme(spacing.20))]" data-testid="inbox-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-[30px] font-semibold text-[#131517]">Inbox</h1>
          <p className="text-[14px] text-[#595C5C] mt-1">Manage your WhatsApp conversations</p>
        </div>
        <Button
          onClick={() => setShowNewConv(true)}
          className="bg-[#131517] hover:bg-[#333537]"
          data-testid="new-conversation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Conversation
        </Button>
      </div>

      <div className="flex gap-6 h-[calc(100%-5.5rem)]">
        {/* Conversation List */}
        <Card className="w-80 flex-shrink-0 border-[#D9E2E5] flex flex-col">
          <CardHeader className="pb-3 border-b border-[#D9E2E5] flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <CardTitle className="text-[16px] font-semibold text-[#131517]">Conversations</CardTitle>
              <button
                onClick={() => fetchConversations(activeSearchRef.current, 0, false)}
                className="text-[#8A8A8B] hover:text-[#595C5C] transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8A8A8B] pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or number..."
                className="w-full h-8 pl-8 pr-8 text-[13px] bg-[#F8F9FA] border border-[#D9E2E5] rounded-md focus:outline-none focus:border-[#595C5C] text-[#131517] placeholder-[#AFB0B0]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#AFB0B0] hover:text-[#595C5C]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {searchQuery.length > 0 && searchQuery.length < 5 && (
              <p className="text-[11px] text-[#AFB0B0] mt-1">
                Type {5 - searchQuery.length} more character{5 - searchQuery.length !== 1 ? 's' : ''} to search
              </p>
            )}
          </CardHeader>
          <div
            ref={convListRef}
            onScroll={handleConvScroll}
            className="flex-1 overflow-y-auto"
          >
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#595C5C]" />
              </div>
            ) : conversations.length > 0 ? (
              <div className="divide-y divide-[#D9E2E5]">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedConv?.id === conv.id
                        ? 'bg-[#131517] text-white'
                        : 'hover:bg-[#EFEFEF]'
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        selectedConv?.id === conv.id ? 'bg-white/20' : 'bg-[#EFEFEF]'
                      }`}>
                        <User className={`w-5 h-5 ${selectedConv?.id === conv.id ? 'text-white' : 'text-[#595C5C]'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p className={`text-[14px] font-medium truncate ${
                            selectedConv?.id === conv.id ? 'text-white' : 'text-[#131517]'
                          }`}>
                            {conv.contact_name || conv.contact_phone}
                          </p>
                          <span className={`text-[10px] flex-shrink-0 ${
                            selectedConv?.id === conv.id ? 'text-white/70' : 'text-[#8A8A8B]'
                          }`}>
                            {formatTime(conv.last_message_at)}
                          </span>
                        </div>
                        <p className={`text-[12px] truncate mt-0.5 ${
                          selectedConv?.id === conv.id ? 'text-white/70' : 'text-[#8A8A8B]'
                        }`}>
                          {conv.last_message_preview || 'No messages yet'}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          {conv.window_status === 'open' ? (
                            <span className={`text-[10px] ${selectedConv?.id === conv.id ? 'text-[#3BAB59]/80' : 'text-[#3BAB59]'}`}>In window</span>
                          ) : conv.window_status === 'unknown' ? (
                            <span className={`text-[10px] ${selectedConv?.id === conv.id ? 'text-amber-300' : 'text-amber-500'}`}>Window unknown</span>
                          ) : (
                            <span className={`text-[10px] ${selectedConv?.id === conv.id ? 'text-white/50' : 'text-[#AFB0B0]'}`}>Expired</span>
                          )}
                          {conv.unread_count > 0 && selectedConv?.id !== conv.id && (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 text-[10px] font-medium text-white bg-[#3BAB59] rounded-full px-1">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Load more indicator */}
                {loadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-[#595C5C]" />
                    <span className="text-[12px] text-[#8A8A8B] ml-2">Loading more…</span>
                  </div>
                )}
                {!hasMore && conversations.length >= PAGE_SIZE && (
                  <div className="text-center py-3">
                    <p className="text-[11px] text-[#AFB0B0]">All conversations loaded</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                {searchQuery ? (
                  <>
                    <Search className="w-10 h-10 mx-auto text-[#D9E2E5] mb-3" />
                    <p className="text-[14px] text-[#8A8A8B]">No results found</p>
                    <p className="text-[12px] text-[#AFB0B0] mt-1">Try a different name or number</p>
                  </>
                ) : (
                  <>
                    <InboxIcon className="w-12 h-12 mx-auto text-[#D9E2E5] mb-3" />
                    <p className="text-[14px] text-[#8A8A8B]">No conversations yet</p>
                    <p className="text-[12px] text-[#AFB0B0] mt-1">Start a new conversation or wait for incoming messages</p>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Chat Area */}
        <Card className="flex-1 border-[#D9E2E5] flex flex-col overflow-hidden">
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <CardHeader className="pb-3 border-b border-[#D9E2E5] flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#EFEFEF] rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-[#595C5C]" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-[16px] font-semibold text-[#131517]">
                      {selectedConv.contact_name || selectedConv.contact_phone}
                    </CardTitle>
                    <p className="text-[12px] text-[#8A8A8B]">{selectedConv.contact_phone}</p>
                  </div>
                  {windowStatus === 'open' ? (
                    <span className="text-[11px] px-2 py-1 rounded bg-[#3BAB59]/10 text-[#3BAB59] border border-[#3BAB59]/20 flex-shrink-0">
                      In Window
                    </span>
                  ) : windowStatus === 'unknown' ? (
                    <span className="text-[11px] px-2 py-1 rounded bg-amber-50 text-amber-600 border border-amber-200 flex-shrink-0">
                      Window Unknown
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-1 rounded bg-[#EB493D]/10 text-[#EB493D] border border-[#EB493D]/20 flex-shrink-0">
                      Window Expired
                    </span>
                  )}
                </div>
              </CardHeader>

              {/* Messages */}
              <div
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                className="flex-1 overflow-y-auto p-4"
              >
                {/* Load older messages indicator */}
                {loadingMoreMessages && (
                  <div className="flex items-center justify-center py-3" data-testid="loading-older-messages">
                    <Loader2 className="w-4 h-4 animate-spin text-[#8A8A8B] mr-2" />
                    <span className="text-[12px] text-[#8A8A8B]">Loading older messages…</span>
                  </div>
                )}
                {hasMoreMessages && !loadingMoreMessages && (
                  <button
                    onClick={loadOlderMessages}
                    className="flex items-center justify-center w-full py-2 mb-2 text-[12px] text-[#595C5C] hover:text-[#131517] transition-colors group"
                    data-testid="load-older-messages-btn"
                  >
                    <div className="h-px bg-[#D9E2E5] flex-1 group-hover:bg-[#595C5C] transition-colors" />
                    <span className="px-3 flex items-center gap-1.5">
                      <RefreshCw className="w-3 h-3" />
                      Load older messages
                    </span>
                    <div className="h-px bg-[#D9E2E5] flex-1 group-hover:bg-[#595C5C] transition-colors" />
                  </button>
                )}
                <div className="space-y-3">
                  {messages.length === 0 && !loadingMoreMessages && (
                    <div className="text-center py-8">
                      <p className="text-[13px] text-[#8A8A8B]">No messages yet</p>
                    </div>
                  )}
                  {messages.map((msg) => (
                    <div
                      key={msg.id || msg._id}
                      className={`flex ${msg.type === 'OUTGOING' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                          msg.type === 'OUTGOING'
                            ? 'bg-[#131517] text-white rounded-br-sm'
                            : 'bg-[#EFEFEF] text-[#131517] rounded-bl-sm'
                        }`}
                        data-testid={`message-${msg.id}`}
                      >
                        <MessageContent message={msg} />
                        <div className={`flex items-center justify-end gap-1 mt-1 ${
                          msg.type === 'OUTGOING' ? 'text-white/60' : 'text-[#8A8A8B]'
                        }`}>
                          <span className="text-[10px]">{formatTime(msg.type === 'OUTGOING' ? (msg.sent_at || msg.created_at) : msg.created_at)}</span>
                          {msg.type === 'OUTGOING' && getStatusIcon(msg)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-[#D9E2E5] flex-shrink-0">
                {/* Window status banners */}
                {windowStatus === 'expired' && (
                  <div className="flex items-start gap-2 mb-3 p-3 bg-[#EB493D]/5 border border-[#EB493D]/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-[#EB493D] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[12px] text-[#EB493D] font-medium">Conversation window expired</p>
                      <p className="text-[11px] text-[#EB493D]/70 mt-0.5">The contact hasn't replied in over 24 hours. Use a template to re-engage.</p>
                    </div>
                  </div>
                )}
                {windowStatus === 'unknown' && (
                  <div className="flex items-start gap-2 mb-3 p-3 bg-amber-50/60 border border-amber-100 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[12px] text-amber-700 font-medium">Window status unknown</p>
                      <p className="text-[11px] text-amber-600 mt-0.5">No reply on record. Message may fail if the window has closed — use a template to be safe.</p>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendTextMessage()}
                    placeholder={windowStatus === 'expired' ? 'Window expired — use template to reply' : 'Type a message...'}
                    disabled={windowStatus === 'expired' || sending}
                    className="border-[#D9E2E5] flex-1"
                    data-testid="message-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setShowTemplateSend(true)}
                    className="border-[#D9E2E5] text-[#595C5C] hover:text-[#131517] px-3 flex-shrink-0"
                    title="Send template message"
                    data-testid="open-template-send-btn"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={sendTextMessage}
                    disabled={windowStatus === 'expired' || !newMessage.trim() || sending}
                    className="bg-[#131517] hover:bg-[#333537] px-3 flex-shrink-0"
                    data-testid="send-message-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <InboxIcon className="w-16 h-16 mx-auto text-[#D9E2E5] mb-4" />
                <p className="text-[16px] text-[#8A8A8B]">Select a conversation</p>
                <p className="text-[13px] text-[#AFB0B0] mt-1">Choose from the list or start a new one</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Modals */}
      <TemplateSendModal
        open={showTemplateSend}
        onClose={() => setShowTemplateSend(false)}
        onSend={sendTemplateMessage}
        templates={templates}
        sending={sending}
      />
      <NewConversationModal
        open={showNewConv}
        onClose={() => setShowNewConv(false)}
        onStart={startNewConversation}
        templates={templates}
        starting={starting}
      />
    </div>
  );
};

export default InboxPage;
